import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { Check, CreditCard, Users, CheckCircle2, Clock, FileText, Star, TagIcon, Mail, Zap } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

export default function Upgrade() {
  const { user } = useAuth() || {};
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const updatePlanMutation = useMutation({
    mutationFn: async (plan: "FREELANCER" | "BUSINESS") => {
      const res = await apiRequest(`/api/user/plan`, "PATCH", { plan });
      return res;
    },
    onSuccess: () => {
      toast({
        title: "Plán aktualizovaný",
        description: "Váš plán bol úspešne aktualizovaný.",
      });
      navigate("/dashboard");
    },
    onError: (error: Error) => {
      toast({
        title: "Chyba pri aktualizácii plánu",
        description: error.message,
        variant: "default",
      });
    }
  });

  const handleUpgrade = (plan: "FREELANCER" | "BUSINESS") => {
    updatePlanMutation.mutate(plan);
  };

  return (
    <div className="page-container py-6 sm:py-8">
      <div className="w-full min-w-0">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="page-title text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">Vyberte si plán, ktorý vám vyhovuje</h1>
          <p className="text-lg text-muted-foreground">
            Aktuálny plán: <span className="ml-1 inline-flex items-center rounded-md border px-2.5 py-0.5 font-medium">{user?.plan || "FREELANCER"}</span>
          </p>
          
          {user?.planExpiresAt && (
            <p className="text-sm text-muted-foreground mt-2">
              Platnosť končí: {new Date(user.planExpiresAt).toLocaleDateString()}
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Freelancer Plan */}
          <Card className={`border-2 relative overflow-hidden ${user?.plan === "FREELANCER" ? "border-primary shadow-md" : "border-muted"}`}>
            {user?.plan === "FREELANCER" && (
              <div className="absolute top-0 right-0">
                <Badge className="rounded-bl-md rounded-tr-md rounded-br-none rounded-tl-none py-1 px-3">
                  Aktuálny plán
                </Badge>
              </div>
            )}
            <CardHeader className="pb-4">
              <div className="flex justify-center mb-2">
                <div className="bg-blue-100 dark:bg-blue-900/30 w-12 h-12 rounded-full flex items-center justify-center">
                  <TagIcon size={24} className="text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <CardTitle className="text-center">Freelancer</CardTitle>
              <div className="mt-3 flex items-baseline justify-center text-3xl font-semibold">
                15€
                <span className="ml-1 text-sm font-normal text-muted-foreground">/mesačne</span>
              </div>
              <CardDescription className="text-center">Pre freelancerov a malé biznisy</CardDescription>
            </CardHeader>
            <Separator />
            <CardContent className="pt-6">
              <ul className="space-y-3">
                <li className="flex items-center">
                  <Users size={16} className="mr-2 text-muted-foreground" />
                  <span>Maximálne <strong>25</strong> klientov</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle2 size={16} className="mr-2 text-muted-foreground" />
                  <span>Neobmedzené úlohy</span>
                </li>
                <li className="flex items-center">
                  <FileText size={16} className="mr-2 text-muted-foreground" />
                  <span>Maximálne <strong>50</strong> faktúr mesačne</span>
                </li>
                <li className="flex items-center">
                  <Mail size={16} className="mr-2 text-green-600" />
                  <span>Základná podpora</span>
                </li>
                <li className="flex items-center">
                  <Zap size={16} className="mr-2 text-green-600" />
                  <span>QR kódy na faktúrach</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter className="pt-2">
              <Button 
                variant={user?.plan === "FREELANCER" ? "outline" : "default"} 
                className="w-full"
                disabled={user?.plan === "FREELANCER" || updatePlanMutation.isPending}
                onClick={() => handleUpgrade("FREELANCER")}
              >
                {user?.plan === "FREELANCER" ? "Váš aktuálny plán" : "Vybrať plán"}
              </Button>
            </CardFooter>
          </Card>

          {/* Business Plan */}
          <Card className={`border-2 relative overflow-hidden ${user?.plan === "BUSINESS" ? "border-primary shadow-md" : "border-muted"}`}>
            {user?.plan === "BUSINESS" && (
              <div className="absolute top-0 right-0">
                <Badge className="rounded-bl-md rounded-tr-md rounded-br-none rounded-tl-none py-1 px-3">
                  Aktuálny plán
                </Badge>
              </div>
            )}
            <div className="absolute top-0 left-0">
              <Badge variant="default" className="rounded-br-md rounded-tl-md rounded-bl-none rounded-tr-none py-1 px-3 bg-gradient-to-r from-yellow-500 to-orange-500">
                Populárny
              </Badge>
            </div>
            <CardHeader className="pb-4">
              <div className="flex justify-center mb-2">
                <div className="bg-yellow-100 dark:bg-yellow-900/30 w-12 h-12 rounded-full flex items-center justify-center">
                  <Star size={24} className="text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
              <CardTitle className="text-center">Business</CardTitle>
              <div className="mt-3 flex items-baseline justify-center text-3xl font-semibold">
                35€
                <span className="ml-1 text-sm font-normal text-muted-foreground">/mesačne</span>
              </div>
              <CardDescription className="text-center">Pre profesionálne agentúry a tímy</CardDescription>
            </CardHeader>
            <Separator />
            <CardContent className="pt-6">
              <ul className="space-y-3">
                <li className="flex items-center">
                  <Users size={16} className="mr-2 text-muted-foreground" />
                  <span><strong>Neobmedzené</strong> klienti</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle2 size={16} className="mr-2 text-muted-foreground" />
                  <span><strong>Neobmedzené</strong> úlohy</span>
                </li>
                <li className="flex items-center">
                  <FileText size={16} className="mr-2 text-muted-foreground" />
                  <span><strong>Neobmedzené</strong> faktúry</span>
                </li>
                <li className="flex items-center">
                  <Mail size={16} className="mr-2 text-green-600" />
                  <span>Tímová spolupráca</span>
                </li>
                <li className="flex items-center">
                  <Zap size={16} className="mr-2 text-green-600" />
                  <span>Prioritná podpora</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter className="pt-2">
              <Button 
                variant={user?.plan === "BUSINESS" ? "outline" : "default"} 
                className="w-full"
                disabled={user?.plan === "BUSINESS" || updatePlanMutation.isPending}
                onClick={() => handleUpgrade("BUSINESS")}
              >
                {user?.plan === "BUSINESS" ? "Váš aktuálny plán" : "Vybrať plán"}
              </Button>
            </CardFooter>
          </Card>
        </div>

        <div className="my-16 bg-muted/50 py-10 px-8 rounded-xl border">
          <h2 className="text-2xl font-bold mb-6 text-center">Detailné porovnanie plánov</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-4 px-4">Funkcia</th>
                  <th className="text-center py-4 px-4 w-[30%]">
                    <div className="flex flex-col items-center">
                      <TagIcon size={20} className="text-blue-600 dark:text-blue-400 mb-2" />
                      <span className="font-bold">Freelancer</span>
                      <span className="text-sm text-muted-foreground">15€/mesačne</span>
                    </div>
                  </th>
                  <th className="text-center py-4 px-4 w-[30%]">
                    <div className="flex flex-col items-center">
                      <Star size={20} className="text-yellow-600 dark:text-yellow-400 mb-2" />
                      <span className="font-bold">Business</span>
                      <span className="text-sm text-muted-foreground">35€/mesačne</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-4 px-4 font-medium">Počet klientov</td>
                  <td className="text-center py-4 px-4">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">25</span>
                      <Progress value={100 * (25 / 25)} className="w-16 h-1.5 mt-1" />
                    </div>
                  </td>
                  <td className="text-center py-4 px-4">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">Neobmedzene</span>
                      <Progress value={100} className="w-16 h-1.5 mt-1" />
                    </div>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4 font-medium">Počet úloh</td>
                  <td className="text-center py-4 px-4">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">Neobmedzene</span>
                      <Progress value={100} className="w-16 h-1.5 mt-1" />
                    </div>
                  </td>
                  <td className="text-center py-4 px-4">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">Neobmedzene</span>
                      <Progress value={100} className="w-16 h-1.5 mt-1" />
                    </div>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4 font-medium">Faktúry mesačne</td>
                  <td className="text-center py-4 px-4">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">50</span>
                      <Progress value={100 * (50 / 50)} className="w-16 h-1.5 mt-1" />
                    </div>
                  </td>
                  <td className="text-center py-4 px-4">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">Neobmedzene</span>
                      <Progress value={100} className="w-16 h-1.5 mt-1" />
                    </div>
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-4 px-4 font-medium">Tímová spolupráca</td>
                  <td className="text-center py-4 px-4 text-muted-foreground">
                    <div className="flex flex-col items-center">
                      <Check className="h-5 w-5 text-red-500" stroke="none" />
                    </div>
                  </td>
                  <td className="text-center py-4 px-4">
                    <div className="flex flex-col items-center">
                      <Check className="h-5 w-5 text-green-500" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-16 p-8 bg-card rounded-xl border shadow">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="shrink-0 w-28 h-28 rounded-full bg-primary/10 flex items-center justify-center">
              <CreditCard className="h-12 w-12 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold mb-2">Potrebujete pomoc s výberom plánu?</h3>
              <p className="text-muted-foreground mb-4">
                Ak si nie ste istí, ktorý plán je pre vás najvhodnejší, kontaktujte nás. Radi vám pomôžeme vybrať plán, ktorý najlepšie zodpovedá vašim potrebám.
              </p>
              <Button variant="outline" className="mr-2">Kontaktujte nás</Button>
              <Button variant="link" className="text-primary">Prečítajte si FAQ</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}